import { Component, Input, OnDestroy, OnInit, } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, Subscription } from 'rxjs';

import { DateTime } from 'luxon';

import { environment } from '@environment';

import { timeFormat } from '@shared/helpers/timeFormat';
import { SiteConfig } from '@shared/models/site-config';
import { MessageService } from '@shared/services/message-service';

import { ShiftContextService } from '../../../../services/shift-context.service';
import { InboundLoadService } from '../../../../services/inbound-load.service';
import { ArriveInboundLoadUpdate, InboundLoad } from '../../../../models/inbound-load';
import { toTimestamp } from '../../../../helpers/to-timestamp';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
    selector: 'app-arrive-inbound',
    templateUrl: './arrive-inbound.component.html',
    styleUrls: ['./arrive-inbound.component.scss']
})
export class ArriveInboundComponent implements OnInit, OnDestroy {

    _load = new BehaviorSubject<InboundLoad>(null);

    @Input() set load(load: InboundLoad) {
        this._load.next(load);
    }

    form = new FormGroup({
        arrivedDate: new FormControl<Date>(null, Validators.required),
        arrivedTime: new FormControl<string>(null, Validators.required),
        trailerId: new FormControl<number>(null, Validators.required)
    });

    updating: boolean = false;
    subscriptions: Subscription[];

    siteTimezone: string = environment.fallbackTimeZone;

    constructor(
        private dialogRef: DynamicDialogRef,
        private config: DynamicDialogConfig,
        private inboundLoadService: InboundLoadService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService) { }

    ngOnInit(): void {
        this._load.next(this.config.data?.load ?? null);
        this.subscriptions = [
            this.shiftContextService.SiteConfig.subscribe(configs => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

            this._load.subscribe(load => {
                if (load) {
                    this.form.setValue({
                        arrivedDate: (load.arrivedTimestamp ?? DateTime.local()).startOf('day').toJSDate(),
                        arrivedTime: timeFormat((load.arrivedTimestamp ?? DateTime.local()).setZone(this.siteTimezone)),
                        trailerId: load.trailerId ?? null
                    });
                }
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    arriveLoad(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Check In Load',
                detail: 'Please correct the listed errors.'
            });

            return;
        }

        const update: ArriveInboundLoadUpdate = {
            arrivedTimestamp: toTimestamp(this.form.controls.arrivedDate.value, this.form.controls.arrivedTime.value, this.siteTimezone),
            trailerId: this.form.controls.trailerId.value
        };

        this.updating = true;
        this.inboundLoadService.arriveInboundLoad(this._load.value.id, update)
            .subscribe({
                next: (load) => {
                    this.updating = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Load Checked In',
                        detail: `Load ${this._load.value.vendorBolNumber} has been checked in.`
                    });

                    this.dialogRef.close(load);
                },
                error: _ => {
                    this.updating = false;
                }
            });
    }
}
